import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '@/services';
import { queryKeys } from '@/services/query-keys';
import { User, GoogleAuthResponse, LoginCredentials } from '@/types';

// Google Auth mutation
export const useGoogleAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (response: GoogleAuthResponse) => {
      const authResponse = await authService.googleAuth(response.credential);

      // Cookie is set by the server — no need to store token client-side

      // Create user object from auth response
      const user: User = {
        id: authResponse.email, // Using email as ID since no ID provided
        email: authResponse.email,
        name: `${authResponse.first_name || ''} ${authResponse.last_name || ''}`.trim() || authResponse.email,
        role: authResponse.role ?? '',
        permissions: authResponse.permissions ?? [],
        lastLoginAt: new Date(),
        onboardingSeenVersion: authResponse.onboarding_seen_version,
      };

      return { user };
    },
    onSuccess: (data) => {
      // Invalidate and refetch user data
      queryClient.setQueryData(queryKeys.auth.currentUser, data.user);
    },
    onError: () => {},
  });
};

// Email/Password Auth mutation
export const useEmailAuth = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      const authResponse = await authService.emailPasswordAuth(credentials.email, credentials.password);

      const user: User = {
        id: authResponse.email,
        email: authResponse.email,
        name: `${authResponse.first_name || ''} ${authResponse.last_name || ''}`.trim() || authResponse.email,
        role: authResponse.role ?? '',
        permissions: authResponse.permissions ?? [],
        lastLoginAt: new Date(),
        onboardingSeenVersion: authResponse.onboarding_seen_version,
      };

      return { user };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.auth.currentUser, data.user);
    },
    onError: () => {},
  });
};
